import React from 'react';
import { FlatList, StyleSheet, Text, View } from 'react-native';
import { useReferenceWindowDimensions } from '../utils/referenceViewport';
import { colors, radius, typography , systemSansFont} from '../theme/tokens';
import { FocusablePressable } from './FocusablePressable';
import { StreamingServiceLogo } from './StreamingServiceLogo';
import { ArrowRight } from 'lucide-react-native';
import { isTelevisionRuntime } from '../utils/platformCapabilities';

type Service = { slug: string; label: string; accent: string; logoFile: string };
type Props = { onPress: (service: Service) => void; onMorePress: () => void };

const SERVICES: Service[] = [
    { slug: 'netflix', label: 'Netflix', accent: '#e50914', logoFile: 'netflix.svg' },
    { slug: 'prime-video', label: 'Prime Video', accent: '#00a8e1', logoFile: 'prime-video.svg' },
    { slug: 'hulu', label: 'Hulu', accent: '#1ce783', logoFile: 'hulu.svg' },
    { slug: 'disney-plus', label: 'Disney+', accent: '#1539c7', logoFile: 'disney-plus.svg' },
    { slug: 'hbo-max', label: 'HBO Max', accent: '#7137c8', logoFile: 'hbo-max.svg' },
    { slug: 'crunchyroll', label: 'Crunchyroll', accent: '#f47521', logoFile: 'crunchyroll.svg' },
];

function ServiceLogo({ service }: { service: Service }) {
    return <StreamingServiceLogo service={service.slug} />;
}

export function StreamingServicesShelf({ onPress, onMorePress }: Props) {
    const { width } = useReferenceWindowDimensions();
    const television = isTelevisionRuntime();
    const pagePadding = television ? 64 : width >= 1280 ? 56 : width > 760 ? 32 : 16;
    return <View style={styles.root}>
            <Text style={[typography.sectionTitle, television ? styles.titleTelevision : width <= 760 ? styles.titleMobile : styles.titleDesktop, { marginHorizontal: pagePadding }]}>Streaming Services</Text>
        <FlatList
            horizontal
            data={SERVICES}
            keyExtractor={(service) => service.slug}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ paddingHorizontal: pagePadding, paddingTop: 16, paddingBottom: 16 }}
            ListFooterComponent={<FocusablePressable accessibilityLabel="View all streaming services" onPress={onMorePress} style={(focused, pressed, hovered) => [styles.tile, television && styles.tileTelevision, width <= 760 && !television && styles.tileCompact, styles.moreTile, (focused || hovered) && styles.moreTileActive, focused && styles.focused, pressed && styles.pressed]}><Text style={styles.moreText}>More</Text><ArrowRight color={colors.text} size={20} /></FocusablePressable>}
            renderItem={({ item }) => <FocusablePressable accessibilityLabel={`Open ${item.label}`} onPress={() => onPress(item)} style={(focused, pressed, hovered) => [styles.tile, television && styles.tileTelevision, width <= 760 && !television && styles.tileCompact, (focused || hovered) && styles.tileActive, focused && styles.focused, pressed && styles.pressed]}>
                <View style={[styles.accent, { backgroundColor: item.accent }]} />
                <ServiceLogo service={item} />
            </FocusablePressable>}
        />
    </View>;
}

const styles = StyleSheet.create({
    root: { marginBottom: 50, paddingTop: 5 },
    titleDesktop: { fontFamily: systemSansFont,  fontSize: 24, lineHeight: 29, fontWeight: '700' },
    titleMobile: { fontFamily: systemSansFont,  fontSize: 18, lineHeight: 23, fontWeight: '700' },
    titleTelevision: { fontFamily: systemSansFont, fontSize: 30, lineHeight: 38, fontWeight: '700' },
    tileCompact: { width: 156, height: 88 },
    tile: { width: 200, height: 112, marginRight: 10, overflow: 'hidden', alignItems: 'center', justifyContent: 'center', borderRadius: radius.medium, borderWidth: StyleSheet.hairlineWidth, borderColor: 'rgba(255,255,255,0.1)', backgroundColor: 'rgba(14,17,24,0.94)' },
    tileTelevision: { width: 240, height: 134 },
    tileActive: { borderColor: 'rgba(255,255,255,0.16)', transform: [{ scale: 1.05 }] },
    moreTile: { flexDirection: 'row', gap: 12 },
    moreTileActive: { borderColor: 'rgba(255,255,255,0.20)', backgroundColor: 'rgba(18,22,31,0.98)', transform: [{ scale: 1.05 }] },
    moreText: { fontFamily: systemSansFont,  color: colors.text, fontSize: 16, lineHeight: 20, fontWeight: '700' },
    pressed: { opacity: 0.82 },
    focused: { borderWidth: 3, borderColor: colors.text, transform: [{ scale: 1.05 }] },
    accent: { position: 'absolute', top: 0, left: 0, right: 0, height: 4 },
    logoPlaceholder: { width: 150, height: 42 },
    logoPlaceholderCompact: { width: 120, height: 34 },
});
